'use client';
import { useEffect, useRef, useState } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import { cn } from '@/app/utils/cn';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';

const DESKTOP_PANEL_ID = 'split-index-panel';
const TAB_ID = (idx) => `split-index-tab-${idx}`;
const MOBILE_PANEL_ID = (idx) => `split-index-mobile-panel-${idx}`;

function normalizeItems(sections) {
    return (
        sections
            ?.filter((section) => section && (section.title || section.headline))
            .map((section, index) => ({
                id: section.identifier || `split-index-${index}`,
                label: section.label || '',
                question: section.title || section.headline || '',
                answer: section.body?.json ?? section.body ?? section._map?.body ?? ''
            })) || []
    );
}

function Answer({ answer }) {
    if (!answer) return null;
    if (typeof answer === 'string') {
        return (
            <p className="sm:text-lg mt-6 text-base leading-relaxed text-custom-muted-foreground">
                {answer}
            </p>
        );
    }
    return (
        <div className="sm:text-lg [&_p]:sm:text-lg mt-6 leading-relaxed text-custom-muted-foreground [&_p]:text-base">
            <DotBlockEditor blocks={answer} />
        </div>
    );
}

function Layout01(props) {
    const { eyebrow, title, subtitle, sections } = props;
    const items = normalizeItems(sections);
    // Desktop always has an active item (drives the side panel); can never be null.
    // Mobile is an accordion — tracks its own state so the currently open row can
    // be tapped to collapse without disturbing the desktop side-panel selection.
    const [activeIndex, setActiveIndex] = useState(0);
    const [mobileOpenIndex, setMobileOpenIndex] = useState(0);
    const [animating, setAnimating] = useState(false);
    const tabRefs = useRef([]);
    const scrollTimeoutRef = useRef(null);
    const animateTimeoutRef = useRef(null);

    useEffect(
        () => () => {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            if (animateTimeoutRef.current) clearTimeout(animateTimeoutRef.current);
        },
        []
    );

    if (!items.length) return null;

    const active = items[activeIndex];

    function selectTab(idx) {
        const isMobile =
            typeof window !== 'undefined' && !window.matchMedia('(min-width: 1024px)').matches;

        if (isMobile) {
            // Accordion toggle: tapping the open row collapses it.
            const next = idx === mobileOpenIndex ? null : idx;
            setMobileOpenIndex(next);
            if (next !== null) setActiveIndex(next);

            // Cancel any pending scroll from a previous tap so rapid taps don't
            // race and land on the wrong header.
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            if (next !== null) {
                // Wait for the grid-rows transition (~300ms) to settle, then
                // align the tapped header. scroll-mt-20 offsets the fixed header.
                scrollTimeoutRef.current = setTimeout(() => {
                    tabRefs.current[next]?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                    scrollTimeoutRef.current = null;
                }, 320);
            }
            return;
        }

        // Desktop: fade the right-side panel content, swap, fade back in.
        if (idx === activeIndex) return;
        if (animateTimeoutRef.current) clearTimeout(animateTimeoutRef.current);
        setAnimating(true);
        animateTimeoutRef.current = setTimeout(() => {
            setActiveIndex(idx);
            setAnimating(false);
            animateTimeoutRef.current = null;
        }, 180);
    }

    function handleKeyDown(e, idx) {
        const last = items.length - 1;
        let next = idx;

        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            next = idx < last ? idx + 1 : 0;
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            next = idx > 0 ? idx - 1 : last;
        } else if (e.key === 'Home') {
            next = 0;
        } else if (e.key === 'End') {
            next = last;
        } else {
            return;
        }

        e.preventDefault();
        tabRefs.current[next]?.focus();
        selectTab(next);
    }

    return (
        <section className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-10 px-5 py-16 sm:px-6 sm:py-24 lg:grid-cols-12">
            <aside className="lg:col-span-5">
                <div className="lg:sticky lg:top-24">
                    {eyebrow && (
                        <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.24em] text-primary">
                            {eyebrow}
                        </p>
                    )}
                    {title && (
                        <h2 className="text-3xl font-semibold tracking-tight text-blue-700 sm:text-4xl">
                            {title}
                        </h2>
                    )}
                    {subtitle && (
                        <p className="mt-3 text-sm text-custom-muted-foreground sm:text-base">
                            {subtitle}
                        </p>
                    )}

                    <ol aria-label={title || 'Questions'} className="mt-8 space-y-1">
                        {items.map((item, idx) => {
                            // Highlighted state = desktop selection (always some
                            // item) merged with mobile expansion (may be null).
                            const isSelected = activeIndex === idx;
                            const isMobileOpen = mobileOpenIndex === idx;
                            return (
                                <li key={item.id}>
                                    <button
                                        id={TAB_ID(idx)}
                                        ref={(el) => (tabRefs.current[idx] = el)}
                                        aria-controls={MOBILE_PANEL_ID(idx)}
                                        aria-expanded={isMobileOpen}
                                        onClick={() => selectTab(idx)}
                                        onKeyDown={(e) => handleKeyDown(e, idx)}
                                        className={cn(
                                            'group flex w-full scroll-mt-20 items-start gap-4 rounded-md px-3 py-3 text-left transition-colors duration-200',
                                            isSelected ? 'bg-fuschia-100' : 'hover:bg-gray-50'
                                        )}>
                                        <span
                                            className={cn(
                                                'flex-1 text-sm font-semibold transition-colors duration-200 sm:text-base',
                                                isSelected ? 'text-fuschia-800' : 'text-gray-700'
                                            )}>
                                            {item.question}
                                        </span>
                                        <IconChevronDown
                                            aria-hidden
                                            size={20}
                                            className={cn(
                                                'mt-1 shrink-0 text-primary transition-transform duration-200 lg:hidden',
                                                isMobileOpen ? 'rotate-180' : ''
                                            )}
                                        />
                                        <span
                                            aria-hidden
                                            className={cn(
                                                'mt-1 hidden text-primary transition-all duration-200 lg:inline',
                                                isSelected
                                                    ? 'translate-x-0 opacity-100'
                                                    : '-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-40'
                                            )}>
                                            →
                                        </span>
                                    </button>
                                    <div
                                        id={MOBILE_PANEL_ID(idx)}
                                        role="region"
                                        aria-labelledby={TAB_ID(idx)}
                                        // `inert` blocks focus AND pointer events on
                                        // descendants of collapsed panels — links
                                        // inside DotBlockEditor content stay
                                        // unreachable via keyboard tabbing.
                                        inert={!isMobileOpen || undefined}
                                        className={cn(
                                            'grid transition-[grid-template-rows,opacity] duration-300 ease-out lg:hidden',
                                            isMobileOpen
                                                ? 'grid-rows-[1fr] opacity-100'
                                                : 'grid-rows-[0fr] opacity-0'
                                        )}>
                                        <div className="overflow-hidden">
                                            <div className="px-3 pb-4 pt-2">
                                                {item.label && (
                                                    <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-primary">
                                                        {item.label}
                                                    </p>
                                                )}
                                                <Answer answer={item.answer} />
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            </aside>

            <div className="hidden lg:col-span-7 lg:block">
                <article
                    id={DESKTOP_PANEL_ID}
                    aria-labelledby={TAB_ID(activeIndex)}
                    className="rounded-2xl border border-gray-200 bg-white p-8 shadow-[0_20px_50px_-24px_rgba(12,18,49,0.18)] sm:p-10">
                    <div
                        className={cn(
                            'transition-all duration-200 ease-out',
                            animating ? 'translate-y-2 opacity-0' : 'translate-y-0 opacity-100'
                        )}>
                        {active.label && (
                            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-primary">
                                {active.label}
                            </p>
                        )}
                        <h3 className="mt-3 text-2xl font-semibold text-blue-700 sm:text-3xl">
                            {active.question}
                        </h3>
                        <Answer answer={active.answer} />
                    </div>
                </article>
            </div>
        </section>
    );
}

const layoutComponents = {
    1: Layout01
};

function SplitIndex(props) {
    const { layout = 1 } = props;
    const LayoutComponent = layoutComponents[layout] || Layout01;
    return <LayoutComponent {...props} />;
}

export default SplitIndex;
